import React, { forwardRef } from 'react';
import { Button, FormControl, FormControlLabel, Checkbox, FormHelperText, RadioGroup, Radio, Typography, Link } from '@mui/material';
import { Controller } from 'react-hook-form';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box } from '@mui/system';
import { parseISO } from 'date-fns';
import { MuiMarkdown } from 'mui-markdown';
import MuiTextField from '@mui/material/TextField';
import SignaturePad from 'react-signature-canvas';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider as LocalizationProvider$1 } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

const CustomizedButton = ({ type = undefined, color = undefined, label = "", subtext, ...rest }) => {
    const properties = {
        className: "navigation",
        type,
        color,
        ...rest,
    };
    return React.createElement(Button, { ...properties },
        label,
        subtext);
};

const CheckboxField = ({ id, control, rules, initialValue, checked, onChange, label, labelStyles, checkboxStyles, error, errorMessage, ...checkBoxProps }) => {
    return (React.createElement(Controller, { name: id, control: control, defaultValue: initialValue ?? false, rules: rules, render: ({ field: fieldProps }) => (React.createElement(FormControl, { error: error },
            React.createElement(FormControlLabel, { label: label ?? "", sx: { ...labelStyles }, control: React.createElement(Checkbox, { id: id, sx: { ...checkboxStyles }, checked: fieldProps.checked || checked, onChange: fieldProps.onChange || onChange, ...checkBoxProps }) }),
            error && React.createElement(FormHelperText, null, errorMessage))) }));
};

const DateField = ({ id, name, label, dateAdapter, adapterLocale, localeText, initialValue, control, rules, format, minDate, maxDate, handleChange, slotProps, ...dateFieldProps }) => {
    return (React.createElement(Box, { id: id, mt: 2 },
        React.createElement(LocalizationProvider, { dateAdapter: dateAdapter, adapterLocale: adapterLocale, localeText: localeText },
            React.createElement(Controller, { name: name, control: control, rules: rules, defaultValue: initialValue, render: (renderProps) => {
                    const fieldValue = renderProps.field.value;
                    const parseISODate = parseISO(fieldValue);
                    const newDateValue = isNaN(parseISODate.getTime())
                        ? new Date(fieldValue)
                        : parseISODate;
                    return (React.createElement(DatePicker, { minDate: minDate, maxDate: maxDate, label: label, format: format, onChange: handleChange, slotProps: slotProps, value: fieldValue ? newDateValue : null, ...dateFieldProps }));
                } }))));
};

const RadioButtonGroup = ({ id, children, color, value, error, handleChange, radioGroupStyles, ...radioProps }) => (React.createElement(React.Fragment, null,
    React.createElement(RadioGroup, { id: id, name: id, key: id, color: color, value: value, onChange: handleChange, sx: { ...radioGroupStyles }, ...radioProps }, children),
    React.createElement(FormHelperText, { error: !!error }, error)));

const RadioButtonGroupItem = ({ key, value, label, onChange, labelStyles, }) => {
    return (React.createElement(FormControlLabel, { key: key, value: value, onChange: onChange, control: React.createElement(Radio, { color: "primary" }), label: label, sx: { ...labelStyles } }));
};

const Text = ({ variant = undefined, color = undefined, children, ...props }) => {
    return (React.createElement(Typography, { variant: variant, color: color, ...props },
        React.createElement(MuiMarkdown, { overrides: {
                a: {
                    component: Link,
                    props: {
                        target: "_blank",
                        style: { color: "inherit", textDecorationColor: "inherit" },
                    },
                },
            } }, children)));
};

const TextField = ({ id, label, textFieldStyles, ...textfieldProps }) => {
    return (React.createElement(MuiTextField, { id: id, label: label, type: "text", sx: textFieldStyles, ...textfieldProps }));
};

/* @ts-ignore */
const { default: Pad } = SignaturePad;
const SignatureField = forwardRef(({ name, control, rules, handleChange, signaturePadOptions, }, ref) => {
    const onChange = () => {
        const signatureWithMimeType = ref.current
            .getTrimmedCanvas()
            .toDataURL("image/png");
        handleChange(signatureWithMimeType);
    };
    return (React.createElement(Controller, { name: name, control: control, rules: rules, render: () => (React.createElement(Pad, { ref: ref, onEnd: onChange, clearOnResize: signaturePadOptions.clearOnResize, ...signaturePadOptions.canvasProps })) }));
});

const SelectField = ({ id, label, disabled, options, value, handleChange, error, ...selectProps }) => {
    return (React.createElement(FormControl, { fullWidth: true, error: !!error },
        React.createElement(InputLabel, null, label),
        React.createElement(Select, { id: id, label: label, disabled: disabled, value: value, onChange: handleChange, ...selectProps }, options?.map((option) => (React.createElement(MenuItem, { key: option.value, value: option.value }, option.label)))),
        error && React.createElement(FormHelperText, null, error)));
};

const ALLOWED_FILE_EXTENSIONS = [
    ".pdf",
    ".docx",
    ".png",
    ".jpg",
    ".jpeg",
];
const FileUpload = ({ id, name, control, rules, documentInputRef, handleChange, acceptExtensions, }) => (React.createElement(Controller, { name: name, control: control, rules: rules, render: () => (React.createElement("input", { accept: acceptExtensions ?? ALLOWED_FILE_EXTENSIONS.join(","), type: "file", id: id, style: { display: "none" }, ref: documentInputRef, onChange: handleChange, multiple: true })) }));

const NumberField = ({ name, control, defaultValue, rules, id, label, disabled, placeholder, error, helperText, ...extraNumberProps }) => {
    return (React.createElement(Controller, { name: name, control: control, defaultValue: defaultValue, rules: rules, render: ({ field: fieldProps }) => (React.createElement(MuiTextField, { id: id, type: "number", placeholder: placeholder, disabled: disabled, error: error, label: label, helperText: helperText, value: fieldProps.value || "", onChange: fieldProps.onChange, ...extraNumberProps })) }));
};

const TimeField = ({ dateAdapter, adapterLocale, localeText, name, control, rules, id, label, handleChange, timeFormat, defaultValue, error, errorMessage, ...extraProps }) => {
    return (React.createElement(LocalizationProvider$1, { dateAdapter: dateAdapter, adapterLocale: adapterLocale, localeText: localeText },
        React.createElement(Controller, { name: name, control: control, rules: rules, render: () => {
                return (React.createElement(TimePicker, { label: label, onChange: handleChange, format: timeFormat, defaultValue: defaultValue, slotProps: {
                        textField: {
                            ...extraProps,
                            id: id,
                            error: error,
                            helperText: errorMessage,
                        },
                    } }));
            } })));
};

export { CheckboxField, CustomizedButton, DateField, FileUpload, NumberField, RadioButtonGroup, RadioButtonGroupItem, SelectField, SignatureField, Text, TextField, TimeField };
